export type ProposalSetupQuickSearchCommand =
  | { readonly kind: `close` }
  | { readonly kind: `ignore` }
  | { readonly kind: `open` }

export type ProposalSetupEscapeCommand =
  | { readonly kind: `close`; readonly to: string }
  | { readonly kind: `ignore` }
  | { readonly kind: `open`; readonly to: string }

/** The parts of a keydown the Escape command reads, whatever the proposal. */
export type ProposalSetupEscapeEvent = {
  readonly defaultPrevented: boolean
  readonly repeat: boolean
  readonly targetIsEditable: boolean
}

const IGNORE_SEARCH: ProposalSetupQuickSearchCommand = { kind: `ignore` }
const IGNORE_ESCAPE: ProposalSetupEscapeCommand = { kind: `ignore` }

/** ⌘K on macOS, Ctrl+K elsewhere, toggles quick search; Escape closes it. */
export const proposalSetupQuickSearchCommand = ({
  altKey,
  ctrlKey,
  defaultPrevented,
  isComposing,
  key,
  metaKey,
  open,
  repeat,
  shiftKey,
}: {
  readonly altKey: boolean
  readonly ctrlKey: boolean
  readonly defaultPrevented: boolean
  readonly isComposing: boolean
  readonly key: string
  readonly metaKey: boolean
  readonly open: boolean
  readonly repeat: boolean
  readonly shiftKey: boolean
}): ProposalSetupQuickSearchCommand => {
  if (defaultPrevented || repeat || isComposing || altKey || shiftKey) {
    return IGNORE_SEARCH
  }

  if (key === `Escape`) {
    return open ? { kind: `close` } : IGNORE_SEARCH
  }

  const chord = (metaKey || ctrlKey) && metaKey !== ctrlKey
  if (!chord || key.toLowerCase() !== `k`) {
    return IGNORE_SEARCH
  }

  return open ? { kind: `close` } : { kind: `open` }
}

const isProposalPath = (pathname: string, proposalPath: string): boolean =>
  pathname === proposalPath || pathname.startsWith(`${proposalPath}/`)

/**
 * Escape opens the Setup rail from the proposal and closes it back to the
 * proposal. A path whose drawer handles its own Escape is left to that drawer.
 */
export const proposalSetupEscapeCommand = ({
  defaultPrevented,
  drawerOwnsEscape,
  pathname,
  proposalPath,
  repeat,
  setupMenuOpen,
  setupMenuPath,
  targetIsEditable,
}: ProposalSetupEscapeEvent & {
  readonly drawerOwnsEscape: boolean
  readonly pathname: string
  readonly proposalPath: string
  readonly setupMenuOpen: boolean
  readonly setupMenuPath: string
}): ProposalSetupEscapeCommand => {
  if (defaultPrevented || repeat || targetIsEditable) {
    return IGNORE_ESCAPE
  }

  if (!isProposalPath(pathname, proposalPath) || drawerOwnsEscape) {
    return IGNORE_ESCAPE
  }

  if (setupMenuOpen) {
    return { kind: `close`, to: proposalPath }
  }

  if (pathname === proposalPath) {
    return { kind: `open`, to: setupMenuPath }
  }

  return IGNORE_ESCAPE
}

export const isEditableEscapeTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false
  }
  if (target.isContentEditable) {
    return true
  }
  switch (target.tagName) {
    case `INPUT`:
    case `TEXTAREA`:
    case `SELECT`:
      return true
    default:
      return false
  }
}
